/**
 * The renderer's server bearer token — a signed, 15-minute credential minted by
 * platform-api from the durable Dovod session cookie (`GET /api/auth/token`).
 *
 * Held in memory only, never in `localStorage` or any other persistent store.
 * The cookie is the credential of record; a persisted copy of the short-lived
 * token would just be a replay artefact with no purpose, since losing it on
 * reload costs nothing more than one extra round trip to re-acquire it.
 *
 * Split into its own leaf module (rather than living in `auth.ts`) so both
 * `auth.ts` (which mints/refreshes the token) and `lib/runtime.ts` (which
 * attaches it to every outgoing server request) can read it without a circular
 * import between the two.
 */
let serverToken: string | null = null;

export function attachServerToken(token: string | null): void {
  serverToken = token;
}

export function currentServerToken(): string | null {
  return serverToken;
}

/** A server token's claims, read without verifying it: the server verifies the token itself. */
function serverTokenClaims(token: string): Record<string, unknown> | null {
  const payload = token.split(".")[1];
  if (payload === undefined) return null;
  try {
    const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
    const bytes = Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")), (c) =>
      c.charCodeAt(0),
    );
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return typeof claims === "object" && claims !== null ? { ...claims } : null;
  } catch {
    return null;
  }
}

/**
 * The account a server token names (`sub`): the window only compares it with
 * the account its stored data belongs to.
 */
export function serverTokenSubject(token: string): string | null {
  const sub = serverTokenClaims(token)?.sub;
  return typeof sub === "string" && sub.length > 0 ? sub : null;
}

/**
 * The member an operator's entry session views the workspace as: the token
 * carries `impersonatedBy` (the operator) and `name` (the member) on such a
 * session alone.
 */
export function serverTokenOperatorEntry(token: string): { memberName: string } | null {
  const claims = serverTokenClaims(token);
  const impersonatedBy = claims?.impersonatedBy;
  if (typeof impersonatedBy !== "string" || impersonatedBy.length === 0) return null;
  return { memberName: typeof claims?.name === "string" ? claims.name : "" };
}
